import {RouteComponentProps, useParams} from "react-router";
import {useForm, useWatch} from "antd/lib/form/Form";
import {useRequest} from "ahooks";
import * as api from "../../apis/consignOrder"
import {ColumnProps} from "antd/lib/table";
import PageHeaderLayout from "../../layout/pageHeaderLayout";
import {
    Button,
    Card,
    Col,
    Form,
    Input,
    InputNumber,
    message,
    Popconfirm,
    Row,
    Space,
    Table,
    Divider as AntDivider
} from "antd";
import styles from "./index.module.css";
import DatePicker from "../../components/DatePicker";
import React, {Fragment, useState} from "react";
import AreaSelect from "../../components/AreaSelect";
import {useHistory} from "react-router-dom";
import {useSelector} from "react-redux";
import {RootState} from "../../models";
import {ConsignmentOrderStatusEnum, PlatformType, SalesOrderStatusEnum} from "../../utils/constants";
import ProductSelectModal from "../../components/ProductSelectModal";
import DealerShippingSelectModal from "../../components/DealerShippingSelectModal";
import withAuth from "../../utils/withAuth";
import Divider from "../../components/Divider";
import useAuth from "../../utils/useAuth";

const ErpSyncFragment = withAuth('erpSync', Fragment);

type RouteParams = {
    id: string
}

const SubmitButton = withAuth('submit', Button);
const EditButton = withAuth('edit', Button);
const CompleteButton = withAuth('complete', Button);
const OAFragment = withAuth('oa', Fragment);
const ClosePopconfirm = withAuth('close', Popconfirm);

function ConsignOrderDetail(props: RouteComponentProps<RouteParams>) {
    const id = useParams<RouteParams>()?.id;
    const history = useHistory();
    const [form] = useForm();
    const province = useWatch('province', form);
    const status = useWatch('status', form);
    const products = useWatch('item', form);
    const platform = useSelector<RootState, RootState['auth']>(state => state.auth)?.user?.platform ?? PlatformType.Dealer;
    const [productSelectedModalVisible, setProductSelectedModalVisible] = useState(false);
    const [dealerShippingModalVisible, setDealerShippingModalVisible] = useState(false);
    const [enableEdit, setEnableEdit] = useState(false);
    const showPrice = useAuth().indexOf('showPrice') > -1 || platform === PlatformType.Admin;

    const {loading: loadingClose, run: runClose} = useRequest(api.closeOrder, {
        manual: true,
        onSuccess(res) {
            getOrder(parseInt(id));
        }
    })
    const {loading: loadingOrder, run: getOrder} = useRequest(api.getOrder, {
        manual: false,
        defaultParams: [parseInt(id)],
        onSuccess(res) {
            form.setFieldsValue(res?.data?.data || {});
        }
    });

    const {loading: sendOAApprovalLoading, run: sendOAApproval} = useRequest(api.sendOAApproval, {
        manual: true,
        onSuccess: (res) => {
            message.success("OA提交成功")
        }
    })

    const {loading: erpSyncLoading, run: doErpSync} = useRequest(api.erpSync, {
        manual: true,
        onSuccess: () => {
            message.success('同步成功')
        }
    })

    const {loading: loadingSubmit, run: runSubmit} = useRequest(api.submitOrder, {
        manual: true,
        onSuccess(res) {
            message.success('提交成功');
            getOrder(parseInt(id));
        }
    });

    const {loading: loadingModifyOrder, run: runModifyOrder} = useRequest(api.modifyOrder, {
        manual: true,
        onSuccess(res) {
            message.success('修改成功');
            setEnableEdit(false);
            getOrder(parseInt(id));
        }
    });

    const {loading: loadingCompleteOrder, run: runCompleteOrder} = useRequest(api.completeOrder, {
        manual: true,
        onSuccess(res) {
            message.success('已成功生成销售订单')
            setEnableEdit(false);
            getOrder(parseInt(id));
        }
    })

    const handleSuitQtyChange = (v: any, index: number) => {
        const curProduct = products[index];
        const suitId = curProduct.suitId;
        [...products || []].filter(p => p.suitId == suitId).forEach((p: any, i) => {
            form.setFieldValue(['item', i, 'suitQty'], v || 0);
            form.setFieldValue(['item', i, 'qty'], p.suitQty * (v || 0));
        });
    }

    const handleSuitCurrentQtyChange = (v: any, index: number) => {
        const curProduct = products[index];
        const suitId = curProduct.suitId;
        [...products || []].filter(p => p.suitId == suitId).forEach((p: any, i) => {
            form.setFieldValue(['item', i, 'currentSuitQty'], v || 0);
            form.setFieldValue(['item', i, 'currentQty'], p.suitProductQty * (v || 0));
        });
    }


    const productColumns: Array<ColumnProps<any>> = [
        {
            dataIndex: 'lineNumber',
            key: 'lineNumber',
            fixed: "left",
            width: 100,
            title: '行号',
            render(v: any, record: any, index) {
                return !enableEdit ? v : <Space>
                    {!!record.suitId ? <Popconfirm title={'删除套餐内商品时，将删除整个套餐，是否确认删除？'}
                                                   onConfirm={() => handleSuitRemove(index)}>
                        <Button>-</Button>
                    </Popconfirm> : <Button onClick={() => handleProductRemove(record, index)}>-</Button>}
                    <Button onClick={handleProductAdd}>+</Button>
                </Space>
            }
        },
        {
            dataIndex: 'suitCode',
            key: 'suitCode',
            title: '套餐编号',
            width: 140
        },
        {
            dataIndex: 'suitName',
            key: 'suitName',
            title: '套餐名称',
            width: 160
        },
        {
            dataIndex: 'productCode',
            key: 'productCode',
            title: '商品编码',
            width: 140
        },
        {
            dataIndex: 'productName',
            key: 'productName',
            title: '商品名称',
            width: 160
        },
        {
            dataIndex: 'specification',
            key: 'specification',
            title: '商品规格',
            width: 140
        },
        {
            dataIndex: 'categoryName',
            key: 'categoryName',
            title: '产品线',
            width: 160
        },
        {
            dataIndex: 'method',
            key: 'method',
            title: '方法',
            width: 100,
        },
        {
            dataIndex: 'useIn',
            key: 'useIn',
            title: '适用机型',
            width: 100,
        },
        {
            dataIndex: 'unit',
            key: 'unit',
            title: '单位',
            width: 80
        },
        {
            dataIndex: 'enableGiveaway',
            key: 'enableGiveaway',
            title: '是否赠品',
            width: 100,
            render(v: any) {
                return v ? '是' : '否'
            }
        },
        ...showPrice ? [
            {
                dataIndex: 'price',
                key: 'price',
                title: '销售价',
                width: 100
            },
        ] : [],
        {
            dataIndex: 'qty',
            key: 'qty',
            title: '数量',
            width: 140,
            render(v: any, record: any, index) {
                return record.suitId ? (
                    <Form.Item name={[index, 'suitQty']} initialValue={0}>
                        <InputNumber step={1} min={1} addonBefore={`${record.suitProductQty} x`}
                                     disabled={!enableEdit || record.enableGiveaway}
                                     onChange={v => handleSuitQtyChange(v, index)} style={{width: '100%'}}/>
                    </Form.Item>
                ) : (<Form.Item name={[index, 'qty']} initialValue={0}>
                    <InputNumber step={1} min={1} disabled={!enableEdit || record.enableGiveaway}
                                 style={{width: '100%'}}/>
                </Form.Item>);
            }
        },
        {
            dataIndex: 'completeQty',
            key: 'completeQty',
            title: '已下单数量',
            width: 140,
        },
        {
            dataIndex: 'currentQty',
            key: 'currentQty',
            title: '本次下单数量',
            width: 140,
            render(v: any, record: any, index) {
                return record.suitId ? (
                    <Form.Item name={[index, 'currentSuitQty']} initialValue={0}>
                        <InputNumber step={1} min={1} addonBefore={`${record.suitProductQty} x`}
                                     disabled={status !== ConsignmentOrderStatusEnum.DELIVERED}
                                     onChange={v => handleSuitCurrentQtyChange(v, index)} style={{width: '100%'}}/>
                    </Form.Item>
                ) : (<Form.Item name={[index, 'currentQty']} initialValue={0}>
                    <InputNumber step={1} min={1} disabled={status !== ConsignmentOrderStatusEnum.DELIVERED}
                                 style={{width: '100%'}}/>
                </Form.Item>);
            }
        }
    ];

    const handleProductAdd = () => {
        setProductSelectedModalVisible(true);
    }

    const handleProductRemove = (record: any, index: number) => {
        if (record.enableGiveaway) {
            message.error('赠品不可删除！');
            return;
        }
        const newProducts = products.filter((_: any, i: number) => i != index);
        form.setFieldValue('item', newProducts);

    }

    const handleSuitRemove = (index: number) => {
        const curProduct = products[index];
        const suitId = curProduct.suitId;
        const newProducts = products.filter((p: any) => p.suitId != suitId);
        form.setFieldValue('item', newProducts);
    }

    const handleModifyOrder = () => {
        form.validateFields().then((values) => {
            values.item = (products || []).filter((p: any) => p.qty > 0);
            runModifyOrder(values);
        })
    }

    const handleComplete = () => {
        const values: any = {orderId: parseInt(id), details: []};
        const productsQty = products.map((p: any) => ({
            detailId: p.id,
            qty: p.currentQty
        })).filter((p: any) => !!p.qty);
        if (!productsQty?.length) {
            message.error('请填写本次下单数量');
            return;
        } else {
            values.details = productsQty;
            runCompleteOrder(values);
        }
    }

    const finishAddressSelect = (keys: Array<any>, records: Array<any>) => {
        if (!records.length) {
            return;
        }
        const address = records[0];
        form.setFieldValue('receiver', address.name);
        form.setFieldValue('receiverPhone', address.phone);
        form.setFieldValue('province', address.provinceId);
        form.setFieldValue('city', address.cityId);
        form.setFieldValue('detailAddress', address.address);
        setDealerShippingModalVisible(false);
    }
    const finishProductAdd = (keys?: Array<number>, records?: Array<any>) => {
        const outSuitProducts = products?.filter((p: any) => !p.suitId) || [];
        const exist = [...records || []].find((p: any) => outSuitProducts.find((pp: any) => pp.productId == p.productId && !pp.enableGiveaway));
        if (exist) {
            message.error('选择的商品已存在，请重新选择');
            return;
        } else {
            const selectedProducts = [...records || []].filter(p => !!p.productId).map(p => ({
                ...p,
                id: undefined,
                qty: 0,
                amount: 0,
                rebateLimit: 0,
                sampleLimit: 0,
                suitId: undefined,
                suitName: undefined,
                enableGiveaways: false,
                productCode: p.code,
                productName: p.name,
                productQuote: p.quote,
                enableSample: false,
                expirationDate: p.expiration,
                categoryName: p.categoryName,
            }));
            form.setFieldValue('item', [...products || [], ...selectedProducts]);
            setProductSelectedModalVisible(false);
        }
    }

    const toolbar = (
        <div className={styles.Toolbar}>
            <Divider>
                {platform == PlatformType.Dealer && !enableEdit && status == ConsignmentOrderStatusEnum.NOT_SUBMITTED &&
                    <SubmitButton type={'link'} loading={loadingSubmit}
                                  onClick={() => runSubmit(parseInt(id))}>提交</SubmitButton>}
                {platform == PlatformType.Dealer && !enableEdit && status == ConsignmentOrderStatusEnum.NOT_SUBMITTED &&
                    <EditButton type={'link'} loading={loadingModifyOrder}
                                onClick={() => setEnableEdit(true)}>修改</EditButton>}
                <OAFragment>
                    {platform == PlatformType.Admin && !enableEdit && status == ConsignmentOrderStatusEnum.SUBMITTED &&
                        <Popconfirm title={'确认OA送审'} onConfirm={() => {
                            sendOAApproval(parseInt(id))
                        }}><Button type={"link"} loading={sendOAApprovalLoading}>OA送审</Button></Popconfirm>}
                </OAFragment>
                {platform === PlatformType.Admin && !enableEdit && status == ConsignmentOrderStatusEnum.SUBMITTED &&
                    <ErpSyncFragment><Button type={"link"} loading={erpSyncLoading}
                                             onClick={() => doErpSync(parseInt(id))}>ERP同步</Button></ErpSyncFragment>}
                {platform == PlatformType.Admin && !enableEdit && status == ConsignmentOrderStatusEnum.DELIVERED &&
                    <CompleteButton type={'link'} loading={loadingCompleteOrder}
                                    onClick={handleComplete}>生成订单</CompleteButton>}
                {!enableEdit && platform == PlatformType.Admin &&
                    <ClosePopconfirm title={'是否确认关闭？'} onConfirm={() => runClose(id)}> <Button
                        loading={loadingClose} type={'link'}>关闭</Button></ClosePopconfirm>}
                <Button type={'link'} onClick={() => enableEdit ? setEnableEdit(false) : history.goBack()}>返回</Button>
            </Divider>
        </div>
    );

    const statusBar = (
        <div style={{fontSize: 14, fontWeight: "normal"}}>
            审核编号： {form.getFieldValue('flowId')}
        </div>
    )

    return <PageHeaderLayout withFooterBar={enableEdit} onCancel={() => setEnableEdit(false)} onOk={handleModifyOrder}>
        <Card title={statusBar} extra={toolbar}>
            <Form form={form} labelCol={{span: 8}} disabled={!enableEdit}>
                <Form.Item name={'id'} noStyle/>
                <Form.Item name={'status'} noStyle/>
                <h3 className={styles.SectionTitle}>基本信息</h3>
                <Row>
                    <Col span={8}>
                        <Form.Item name={'code'} label={'寄售申请单号'}>
                            <Input disabled/>
                        </Form.Item>
                    </Col>
                    <Col span={8}>
                        <Form.Item name={'orderDate'} label={'订单日期'}>
                            <DatePicker style={{width: '100%'}} disabled/>
                        </Form.Item>
                    </Col>
                    <Col span={8}>
                        <Form.Item name={'dealerName'} label={'客户'}>
                            <Input disabled/>
                        </Form.Item>
                    </Col>
                    <Col span={8}>
                        <Form.Item name={'hospitalFullName'} label={'终端医院全称'}>
                            <Input disabled/>
                        </Form.Item>
                    </Col>
                    <Col span={8}>
                        <Form.Item name={'salesManName'} label={'销售人员'}>
                            <Input disabled/>
                        </Form.Item>
                    </Col>
                    <Col span={8}>
                        <Form.Item name={'businessManName'} label={'商务人员'}>
                            <Input disabled/>
                        </Form.Item>
                    </Col>
                    <Col span={8}>
                        <Form.Item name={'expectDeliveryTime'} label={'期望日期'}>
                            <DatePicker disabled/>
                        </Form.Item>
                    </Col>
                    <Col span={8}>
                        <Form.Item name={'receiver'} label={'收货人'}>
                            <Input disabled/>
                        </Form.Item>
                    </Col>
                    <Col span={8}>
                        <Form.Item name={'receiverPhone'} label={'收货电话'}>
                            <Input disabled/>
                        </Form.Item>
                    </Col>
                    <Col span={8}>
                        <Form.Item name={'province'} label={'省'}>
                            <AreaSelect/>
                        </Form.Item>
                    </Col>
                    <Col span={8}>
                        <Form.Item name={'city'} label={'市'}>
                            <AreaSelect type={'city'} parent={province}/>
                        </Form.Item>
                    </Col>
                    <Col span={8}>
                        <Form.Item name={'detailAddress'} label={'详细地址'}>
                            <Input addonAfter={enableEdit &&
                                <a onClick={() => setDealerShippingModalVisible(true)}>选取</a>}/>
                        </Form.Item>
                    </Col>
                    <Col span={8}>
                        <Form.Item name={'currency'} label={'币种'}>
                            {/*<Select url={''} getData={data => data?.data || []} textField={'name'} valueField={'id'}/>*/}
                            <Input disabled/>
                        </Form.Item>
                    </Col>
                    <Col span={24}>
                        <Form.Item name={'remark'} label={'备注'} labelCol={{span: 2}}>
                            <Input.TextArea rows={3}/>
                        </Form.Item>
                    </Col>
                </Row>
                <h3 className={styles.SectionTitle}>商品信息</h3>
                <Form.List name={'item'}>
                    {fields => (
                        <Table className={styles.ProductTable} columns={productColumns}
                               dataSource={form.getFieldValue('item')?.length ? form.getFieldValue('item') : [{}]}
                               pagination={false} scroll={{x: '100%'}}/>
                    )}
                </Form.List>
            </Form>
            <div className={styles.ProductSummary}>
                <br/>
                <table>
                    <tr style={{fontSize: 'large'}}>
                        <td>订单合计：</td>
                        <td style={{color: 'var(--danger-color)'}}>{form.getFieldValue('productTotalAmount')}</td>
                    </tr>
                </table>
                <br/>
            </div>
            <ProductSelectModal
                multiple
                url={'/dealerProduct/?inContract=true'}
                open={productSelectedModalVisible}
                onCancel={() => setProductSelectedModalVisible(false)}
                onComplete={finishProductAdd}/>
            {dealerShippingModalVisible && <DealerShippingSelectModal
                multiple={false}
                open={dealerShippingModalVisible}
                onCancel={() => setDealerShippingModalVisible(false)}
                onComplete={finishAddressSelect}/>}
        </Card>
    </PageHeaderLayout>
}

export default ConsignOrderDetail;
